'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/linear-algebra'

const MatMulStepper = dynamic(() => import('../_components/widgets/LinearWidgets').then(m => m.MatMulStepper), { ssr: false })
const LayerAsMatrix = dynamic(() => import('../_components/widgets/LinearWidgets').then(m => m.LayerAsMatrix), { ssr: false })

export default function MatrixMultiplication() {
  return (
    <Lesson>
      <Step>
        <H>Many dot products at once</H>
        <P>
          Multiplying two matrices is just doing a lot of dot products, neatly organised. To get the entry in row{' '}
          <M>i</M>, column <M>j</M> of the answer: take <strong>row i of the first matrix</strong>, dot it with{' '}
          <strong>column j of the second</strong>.
        </P>
        <MB>{'C_{ij} = \\sum_{k} A_{ik}\\, B_{kj}'}</MB>
        <Figure n="3.6" caption="Watch each cell of C being computed: one row of A, one column of B, one dot product.">
          <MatMulStepper />
        </Figure>
      </Step>

      <Step gate="q-shape">
        <H>Shapes have to fit</H>
        <P>
          A row of A has to be as long as a column of B, or the dot product doesn’t work. So an <M>{'n \\times k'}</M> matrix can
          only multiply a <M>{'k \\times m'}</M> matrix — the inner numbers must match — and the result is{' '}
          <M>{'n \\times m'}</M>:
        </P>
        <MB>{'(n \\times \\accent{k}) \\cdot (\\accent{k} \\times m) \\rightarrow (n \\times m)'}</MB>
        <Quiz
          id="q-shape"
          question={<>You multiply a <M>{'64 \\times 768'}</M> matrix by a <M>{'768 \\times 3072'}</M> matrix. What shape is the result?</>}
          options={[{ label: <M>{'64 \\times 3072'}</M>, correct: true }, { label: <M>{'768 \\times 768'}</M>, why: 'The matching inner size (768) disappears; the outer sizes remain.' }, { label: 'They can’t be multiplied', why: 'The inner sizes are both 768, so they fit.' }]}
          explain="The inner 768s match and vanish. (These are real shapes: a batch of 64 tokens going through a GPT-2 feed-forward layer.)"
        />
        <Callout kind="warn">
          Shape mismatches are the #1 bug in machine-learning code. When something breaks, the first question is always: “what
          shape is everything?”
        </Callout>
      </Step>

      <Step gate="q-order">
        <H>Order matters</H>
        <P>
          With ordinary numbers, <M>{'3 \\times 5 = 5 \\times 3'}</M>. Not with matrices. Multiplying two matrices{' '}
          <strong>chains their transformations</strong>, and rotating-then-shearing is different from shearing-then-rotating.
        </P>
        <Exercise id="order-matters" title="AB ≠ BA" {...EX.orderMatters} />
        <Quiz
          id="q-order"
          question={<>In <M>{'(AB)\\mathbf{v}'}</M>, which transformation is applied to v first?</>}
          options={[{ label: 'B', correct: true }, { label: 'A', why: 'Read right to left: B is next to v, so it acts first.' }, { label: 'Both at the same time', why: 'They chain: one, then the other.' }]}
          explain="(AB)v = A(Bv): B transforms v, then A transforms the result — just like f(g(x)) applies g first."
        />
      </Step>

      <Step gate="mat-mul">
        <H>Write matrix multiplication</H>
        <Exercise
          id="mat-mul"
          title="transpose and matMul"
          {...EX.matMul}
          hints={[
            <>
              Transpose: the new row j is <C>{'A.map(row => row[j])'}</C>, for each column j.
            </>,
            <>
              matMul needs three nested loops: rows i of A, columns j of B, and an inner loop over k adding{' '}
              <C>A[i][k] * B[k][j]</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-gpu">
        <H>A layer is a matrix multiply</H>
        <P>
          A layer of neurons: every output neuron is a dot product between its row of weights and the inputs. Stack the rows and
          you get a matrix — one multiplication computes the entire layer.
        </P>
        <Figure n="3.7" caption="Three inputs, two output neurons. Line thickness is weight size; green is positive, pink negative. Each output is one row of the matrix dotted with the inputs.">
          <LayerAsMatrix />
        </Figure>
        <P>
          Better still: put a whole <em>batch</em> of examples in the rows of a matrix X, and <M>{'XW'}</M> runs the layer on all
          of them in a single multiply.
        </P>
        <Exercise id="batch-layer" title="A layer on a batch" {...EX.batchLayer} />
        <Quiz
          id="q-gpu"
          question="Why are graphics chips (GPUs) so good for AI?"
          options={[
            { label: 'They’re designed to do huge numbers of independent multiply-adds in parallel — exactly what matrix multiplication needs.', correct: true },
            { label: 'They have special AI circuits that understand language.', why: 'There’s no understanding inside — just extremely fast arithmetic.' },
            { label: 'They have more memory than CPUs.', why: 'Memory helps, but the key is thousands of cores doing dot products simultaneously.' },
          ]}
          explain="Every cell of a matrix product can be computed independently. Drawing 3D graphics needed exactly this, and it turned out deep learning does too."
        />
        <Callout kind="why">
          You now know linear algebra’s core: vectors, dot products, matrices, matrix multiplication. Next, the other half of
          the engine: <strong>calculus</strong> — how to know which way to nudge all those numbers.
        </Callout>
      </Step>
    </Lesson>
  )
}
